import { Link } from "react-router-dom";
import { OFFER_ORDER, OFFERS } from "../data/offers";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

export function HomePage() {
  useDocumentTitle("Pilot Mastery Academy — Home");

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <img
          src="/images/home/hero.jpg"
          alt="Aircraft flying at golden hour — professional aviation"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(240,180,41,0.25), transparent), radial-gradient(ellipse 60% 40% at 100% 50%, rgba(94,184,232,0.12), transparent), linear-gradient(to top, rgba(7,13,24,1) 0%, transparent 50%)",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:py-28">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-signal-sky">Competence · Control · Community</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            Sharper decisions. Clearer wealth. Confident career moves.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300">
            Pilot Mastery Academy delivers domain-specific frameworks—not generic self-help. Built for professional pilots
            who need flight-deck clarity in money, stress, and transition.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/offers/decision-mastery"
              className="inline-flex items-center justify-center rounded-lg bg-signal-amber px-6 py-3 text-sm font-semibold text-deck-deep shadow-lift transition hover:bg-amber-300"
            >
              Explore Decision cohort
            </Link>
            <Link
              to="/offers/financial-freedom"
              className="inline-flex items-center justify-center rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white transition hover:border-signal-sky hover:text-signal-sky"
            >
              View Wealth program
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white transition hover:border-signal-sky hover:text-signal-sky"
            >
              Read the Journal
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="font-display text-2xl font-semibold text-white sm:text-3xl">Programs</h2>
        <p className="mt-2 max-w-2xl text-slate-400">
          Three premium offers. Canonical pricing and modules live in the repository PRDs—this site summarizes for
          discovery.
        </p>
        <ul className="mt-10 grid gap-6 md:grid-cols-3">
          {OFFER_ORDER.map((key) => {
            const o = OFFERS[key];
            return (
              <li
                key={o.slug}
                className="flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-deck-panel/60 shadow-lift backdrop-blur-sm"
              >
                <img
                  src={`/images/offers/${o.slug}.jpg`}
                  alt={o.name}
                  className="h-44 w-full object-cover"
                />
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-xs font-medium uppercase tracking-wider text-signal-sky">Offer #{o.id}</span>
                  <h3 className="mt-2 font-display text-xl font-semibold text-white">{o.name}</h3>
                  <p className="mt-3 flex-1 text-sm text-slate-400">{o.headline}</p>
                  <p className="mt-4 font-medium text-signal-amber">{o.price}</p>
                  <Link
                    to={`/offers/${o.slug}`}
                    className="mt-6 inline-flex text-sm font-semibold text-signal-sky hover:text-sky-300"
                  >
                    Program details →
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="border-t border-white/10 bg-deck-panel/30 py-16">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="font-display text-2xl font-semibold text-white">Why pilots, why now</h2>
          <div className="mt-8 grid gap-8 sm:grid-cols-3">
            {[
              {
                title: "High responsibility",
                body: "You operate with nonlinear careers and complex finances—generic creators miss the context.",
              },
              {
                title: "Evidence over hype",
                body: "We prefer frameworks, scenarios, and citations over vague superlatives.",
              },
              {
                title: "Compliance-aware",
                body: "Clear boundaries on tax education, medical adjacent topics, and safety content.",
              },
            ].map((b) => (
              <div key={b.title} className="rounded-xl border border-white/5 bg-deck-deep/50 p-5">
                <h3 className="font-medium text-signal-amber">{b.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{b.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/about" className="font-semibold text-signal-sky hover:underline">
              Read our positioning →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
